import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DialogCloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/AddOutlined";
import {
  navTarget,
  scopeReady,
  searchNav,
} from "../../shared/navigationRegistry";
import useDebouncedValue from "../../shared/useDebouncedValue";
import { resolveViewedYearId } from "../../shared/schoolYear";
import { useFixedSidebar } from "../../contexts/FixedSidebarContext";
import { useBookmarks } from "../../contexts/BookmarksContext";
import BookmarksGrid from "./BookmarksGrid";
import SchoolNavMenu from "./SchoolNavMenu";
import SchoolNavMenuMobile from "./SchoolNavMenuMobile";
import DashboardNavToggle from "./DashboardNavToggle";
import DashboardLockLink from "./DashboardLockLink";
import DashboardSearchField from "./DashboardSearchField";
import DashboardSearchResults from "./DashboardSearchResults";
import DashboardShortcutHint from "./DashboardShortcutHint";
import dashboardButtonSx from "./dashboardButtonSx";
import { NavDrawerContext } from "../shared/NavDrawerButton";

// Mobile-only entrance: the launchpad slides up from the bottom edge like a
// sheet. Desktop keeps the default fade. Hoisted (and ref-forwarding, as MUI
// transitions require) so it is not rebuilt on every render.
const SlideUpTransition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));
SlideUpTransition.displayName = "SlideUpTransition";

// Four 116px bookmark tiles on a 12px gutter make the grid 500px wide; the
// content column adds 24px of padding on each side, and the paper 24px more on
// top of that, which is what fixes the dialog at 577px.
const GRID_WIDTH = 500;
const PAPER_WIDTH = 577;
// The paper hangs this far below the top of the viewport (it is anchored, not
// centered - see the container alignment below) and has to leave the same gap
// at the bottom, so its ceiling is the viewport less both gaps. Left at the
// default `calc(100% - 64px)` the height is measured from the top of the
// container and ignores the offset, so a tall body runs off the bottom edge.
const PAPER_OFFSET_Y = "9vh";
const PAPER_MAX_HEIGHT = `calc(100% - ${PAPER_OFFSET_Y} * 2)`;
// Hairlines on the gradient: white knocked back far enough to separate the
// sections without drawing a hard line across the color.
const DIVIDER_COLOR = "rgba(255, 255, 255, 0.35)";

export default function DashboardMenu({
  open,
  onClose,
  initialManage = false,
  appProps,
  schoolSlug,
  schoolYearId,
  navigationMode = "spa",
}) {
  const spaNavigate = useNavigate();
  const theme = useTheme();
  // Match the sidebar's desktop/mobile split so the nav section inside the
  // dialog renders the same format the user sees in the drawer/sidebar.
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const schoolId = appProps?.school?.id ?? null;
  const {
    enabled: sidebarLocked,
    available: sidebarAvailable,
    setEnabled: setSidebarLocked,
  } = useFixedSidebar();

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);

  // Bookmarks live in a shared context (seeded once from appProps) so edits here
  // also re-render the Home screen's grid without a reload.
  const { bookmarkIds, setBookmarkIds, persistBookmarks } = useBookmarks();
  // Latest bookmarkIds, readable from the open-transition effect without making
  // that effect refire (and re-decide navHidden) on every add/remove.
  const bookmarkIdsRef = useRef(bookmarkIds);
  bookmarkIdsRef.current = bookmarkIds;
  const [manageMode, setManageMode] = useState(false);
  // True while a manage-mode edit (reorder/delete) is being persisted, so the
  // "Ok" button can show a spinner and block dismiss until the save settles.
  const [saving, setSaving] = useState(false);
  // Persist while tracking in-flight count so the spinner spans rapid edits.
  const savingCount = useRef(0);
  const saveBookmarks = (ids) => {
    savingCount.current += 1;
    setSaving(true);
    return persistBookmarks(ids).finally(() => {
      savingCount.current -= 1;
      if (savingCount.current === 0) setSaving(false);
    });
  };
  // Id of a just-added bookmark, briefly highlighted (and scrolled into view)
  // so the user sees the add land even when the grid is scrolled out of sight.
  const [highlightId, setHighlightId] = useState(null);
  // Nav hidden/shown: decided at open time from bookmark presence and never
  // auto-toggled while the user adds/removes bookmarks during the session.
  const [navHidden, setNavHidden] = useState(true);

  // Default year derivation, mirroring SchoolNavMenu - links resolve against
  // the staff's last-selected year, then the default school year (no selectors
  // in the dialog anymore).
  const yearId = resolveViewedYearId(appProps, schoolYearId);

  const ctx = {
    schoolId,
    schoolSlug,
    school: appProps?.school ?? null,
    permissions: appProps?.permissions ?? {},
    yearId,
  };

  // Reset transient UI state on the closed -> open transition and decide the
  // nav-hidden default from the CURRENT bookmarks (hidden once any bookmark
  // exists, shown while there are none). Decided only here so adding/removing
  // bookmarks mid-session never flips nav visibility - with the one exception
  // below.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setQuery("");
      // Start in manage mode when asked (the Home "Manage bookmarks" link), but
      // only if there is something to manage; otherwise fall back to normal.
      const startManage = initialManage && bookmarkIdsRef.current.length > 0;
      setManageMode(startManage);
      setNavHidden(bookmarkIdsRef.current.length > 0);
    }
    wasOpen.current = open;
  }, [open, initialManage]);

  // Deleting every bookmark leaves nothing to fall back on: the grid is empty
  // and the toggle that would bring the navigation back is hidden along with
  // it, so the dialog would sit blank until it was closed and reopened. Show
  // the navigation again once the user leaves manage mode ("Ok"), which lands
  // them in the same state as opening the dialog with no bookmarks. Held until
  // then so the body does not slide open under them mid-delete.
  useEffect(() => {
    if (!manageMode && bookmarkIds.length === 0) setNavHidden(false);
  }, [manageMode, bookmarkIds.length]);

  const handleAddBookmark = (id) => {
    setBookmarkIds((prev) => {
      if (prev.includes(id)) return prev; // dedupe
      const next = [...prev, id]; // append to end
      persistBookmarks(next);
      return next;
    });
    setHighlightId(id);
  };

  // Ref into the bookmarks grid so we can hand the keyboard to its first tile
  // once the dialog finishes opening (see the transition onEntered below).
  const gridRef = useRef(null);
  // The element that wraps that grid, measured to find where the bookmarks end
  // when scrolling the navigation into view.
  const gridBoxRef = useRef(null);
  // The dialog body: the only scroll container in here, so every programmatic
  // scroll below goes through it.
  const dialogContentRef = useRef(null);
  // The search input, focused from the transition callbacks below.
  const searchRef = useRef(null);
  // Focus the search field without letting the browser scroll to reveal it.
  // While the navigation section is sliding open its wrapper is clipped
  // (overflow: hidden), and a plain focus makes the browser scroll that clipped
  // box - the field renders half cut off and then drops into place when the
  // slide finishes. preventScroll keeps it still.
  const focusSearch = () => searchRef.current?.focus({ preventScroll: true });

  // After the open transition settles (so MUI's focus trap has already run),
  // land the keyboard somewhere useful: the first bookmark, so arrow keys
  // navigate the grid straight away, or the search field when there are no
  // bookmarks (the navigation is shown in that case). Skipped on mobile (no
  // keyboard) and in manage mode (dnd owns the keys).
  const focusFirstBookmark = () => {
    if (isMobile) return;
    if (bookmarkIds.length === 0) {
      focusSearch();
      return;
    }
    if (manageMode) return;
    gridRef.current?.focusFirst();
  };

  // Bring the navigation into view the moment it finishes sliding open: the
  // body scrolls so it starts where the bookmarks end, putting the toggle
  // ("Hide navigation") at the top with its usual breathing room. A body that
  // only just overflows lands short of that and keeps the last row of tiles
  // half in view - there is no scroll length left to give. Runs on entered
  // rather than on the click: until the section has its full height there is
  // nothing to scroll at all and the browser clamps the request to 0.
  const scrollPastBookmarks = () => {
    const body = dialogContentRef.current;
    const grid = gridBoxRef.current;
    if (!body || !grid) return;
    const top =
      grid.getBoundingClientRect().bottom -
      body.getBoundingClientRect().top +
      body.scrollTop;
    body.scrollTo({ top, behavior: "smooth" });
  };

  // Navigation shown: park the keyboard in the search field (desktop only, and
  // without the browser scrolling to reveal it) and scroll the body down to
  // the section itself.
  const handleNavShown = () => {
    if (!isMobile) focusSearch();
    scrollPastBookmarks();
  };

  const handleManageStart = () => setManageMode(true);
  // Every edit in manage mode (reorder, delete) persists immediately, so there
  // is nothing to save or revert: "Ok" just leaves manage mode.
  const handleManageDone = () => setManageMode(false);

  const handleNavigate = (node) => {
    const target = navTarget(node, ctx);
    if (!target || !scopeReady(target, ctx)) return;
    const url = target.resolve(ctx);
    if (navigationMode === "browser") {
      window.location.assign(url);
    } else if (!target.v1) {
      spaNavigate(url);
    } else {
      window.location.href = url;
    }
    onClose();
  };

  const searchActive = debouncedQuery.trim().length > 0;
  const results = useMemo(
    () => (searchActive ? searchNav(debouncedQuery, ctx).slice(0, 30) : []),
    [searchActive, debouncedQuery, ctx]
  );

  // ---- Search-results keyboard nav (bookmarks grid owns its own) --------
  const resultRefs = useRef([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  useEffect(() => {
    setFocusedIndex(0);
  }, [results.length, searchActive]);

  const focusResult = (i) => {
    setFocusedIndex(i);
    resultRefs.current[i]?.focus();
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown" && results.length > 0) {
      e.preventDefault();
      focusResult(0);
    } else if (e.key === "Enter" && results[focusedIndex]) {
      e.preventDefault();
      handleNavigate(results[focusedIndex].node);
    }
  };

  const onResultKeyDown = (e) => {
    if (e.key === "/") {
      e.preventDefault();
      searchRef.current?.focus();
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleNavigate(results[focusedIndex]?.node);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (focusedIndex + 1 < results.length) focusResult(focusedIndex + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (focusedIndex === 0) searchRef.current?.focus();
      else focusResult(focusedIndex - 1);
    }
  };

  // When a bookmark is just added, scroll the dialog back to the top so the
  // bookmarks grid (and the highlighted new tile) is in view, even if the user
  // was scrolled down in the menu when they clicked the + .
  useEffect(() => {
    if (highlightId) {
      dialogContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [highlightId]);

  // The reused nav menus read their close handler from NavDrawerContext; supply
  // one that closes this dialog (there is no real drawer here). Memoized so the
  // provider value is reference-stable across renders.
  const navDrawerValue = useMemo(() => ({ close: onClose }), [onClose]);

  const renderNavBody = () => {
    if (searchActive) {
      return (
        <DashboardSearchResults
          results={results}
          focusedIndex={focusedIndex}
          setItemRef={(i, el) => {
            resultRefs.current[i] = el;
          }}
          onFocus={(i) => setFocusedIndex(i)}
          onKeyDown={onResultKeyDown}
          onActivate={handleNavigate}
          onAddBookmark={handleAddBookmark}
          bookmarkedIds={bookmarkIds}
          ctx={ctx}
        />
      );
    }
    const NavMenu = isMobile ? SchoolNavMenuMobile : SchoolNavMenu;
    return (
      <NavMenu
        appProps={appProps}
        schoolSlug={schoolSlug}
        schoolYearId={schoolYearId}
        navigationMode={navigationMode}
        onAddBookmark={handleAddBookmark}
        bookmarkedIds={bookmarkIds}
        onGradient
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      // Anchor the paper near the top of the viewport instead of centering it
      // vertically. A centered dialog re-centers as it grows, so showing the
      // navigation dragged the bookmarks and the search field ~265px up the
      // screen while the section slid down - two motions at once. Anchored, the
      // body above the fold holds still and only the new section moves.
      sx={{
        "& .MuiDialog-container": {
          alignItems: { xs: "center", sm: "flex-start" },
        },
      }}
      slots={isMobile ? { transition: SlideUpTransition } : undefined}
      slotProps={{
        paper: {
          sx: {
            position: "relative",
            overflow: "visible",
            // Sized so exactly four bookmark tiles (116px on a 12px gutter) fit
            // the content column. Mobile keeps the theme's full-screen sheet.
            width: { xs: "100%", sm: PAPER_WIDTH },
            maxWidth: { xs: "100%", sm: PAPER_WIDTH },
            // Sits below the top edge rather than centered - see the container
            // alignment above. The ceiling keeps the same gap at the bottom, so
            // the body scrolls inside the paper instead of the paper growing
            // past the viewport. Mobile keeps the theme's full-screen sheet.
            mt: { xs: 0, sm: PAPER_OFFSET_Y },
            maxHeight: { sm: PAPER_MAX_HEIGHT },
            borderRadius: { xs: 0, sm: "26px" },
            border: "1px solid rgba(255, 255, 255, 0.7)",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.25)",
            background: theme.custom.brandGradient,
            backdropFilter: "none",
            WebkitBackdropFilter: "none",
          },
        },
        transition: {
          onEntered: focusFirstBookmark,
          ...(isMobile && { timeout: { enter: 350, exit: 300 } }),
        },
      }}
    >
      {/* Desktop: a white floating close button straddling the dialog's
          top-right corner, half outside the paper (mobile uses the bottom
          actions bar below). Same style as the report card preview overlay's
          close button. */}
      {!isMobile && (
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: -20,
            right: -20,
            zIndex: 1,
            background: "#FFFFFF",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            "&:hover": { background: "#FFFFFF" },
          }}
        >
          <DialogCloseIcon />
        </IconButton>
      )}
      <DialogContent
        ref={dialogContentRef}
        sx={{
          // No bottom padding here on purpose - see the spacer at the end of
          // this block. Once the body is tall enough to scroll, a scroll
          // container's bottom padding is left out of the scrollable area, so
          // the last row ends up flush against the dialog's edge.
          p: "36px 24px 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "20px",
        }}
      >
        {/* Bookmarks grid (top), held to exactly four tile tracks so it stays
            centered under the wider content column below it. */}
        <Box ref={gridBoxRef} sx={{ width: "100%", maxWidth: GRID_WIDTH }}>
          <BookmarksGrid
            ref={gridRef}
            bookmarkIds={bookmarkIds}
            ctx={ctx}
            isMobile={isMobile}
            manageMode={manageMode}
            variant="frosted"
            highlightId={highlightId}
            onHighlightEnd={() => setHighlightId(null)}
            saving={saving}
            onReorder={(next) => {
              setBookmarkIds(next);
              saveBookmarks(next);
            }}
            onRemove={(id) =>
              setBookmarkIds((prev) => {
                const next = prev.filter((x) => x !== id);
                saveBookmarks(next);
                return next;
              })
            }
            onActivate={handleNavigate}
            onLongPress={handleManageStart}
            onManageStart={handleManageStart}
            onManageDone={handleManageDone}
          />
        </Box>

        {/* Fold the navigation away / bring it back. Only offered once a
            bookmark exists to fall back to - with an empty grid, hiding the
            navigation would leave nothing behind. */}
        {bookmarkIds.length > 0 && (
          <DashboardNavToggle
            hidden={navHidden}
            onToggle={() => setNavHidden((prev) => !prev)}
          />
        )}

        {/* Collapse animates the section's height, so showing the navigation
            slides it down and hiding it rolls it back up (the dialog resizes
            with it) instead of the body snapping between two layouts. Unmounted
            while hidden so the search field keeps its autoFocus on the way back
            in and the nav tree is not built for a body nobody can see. */}
        <Collapse
          in={!navHidden}
          timeout={300}
          unmountOnExit
          onEntered={handleNavShown}
          // flexShrink: 0 matters here. Once the body is taller than the capped
          // paper, this flex item would otherwise be squeezed - and because a
          // collapsing wrapper clips (overflow: hidden), the squeeze silently
          // cut the tail of the section off flush with the dialog's bottom edge
          // instead of letting the content scroll.
          sx={{ width: "100%", flexShrink: 0 }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <DashboardSearchField
              inputRef={searchRef}
              // Focus is handled by the callers above once the slide (or the
              // dialog's own open transition) has finished, so the field is not
              // scrolled into view while its wrapper is still clipped.
              autoFocus={false}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
              onClear={() => {
                setQuery("");
                searchRef.current?.focus();
              }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "#FFFFFF",
                fontSize: 14,
                letterSpacing: "-0.5px",
              }}
            >
              Add bookmarks with the
              <AddIcon sx={{ fontSize: 18 }} />
              on any item below.
            </Box>

            <Box sx={{ width: "100%" }}>
              <Divider sx={{ borderColor: DIVIDER_COLOR }} />

              {/* No ceiling of its own: the tree (and the search results that
                  replace it) grows to whatever it needs, so the dialog body is
                  the only thing that scrolls instead of nesting a second
                  scrollbar inside the first. NavDrawerContext supplies the
                  reused nav menus' close handler so an item click closes this
                  dialog (there is no real drawer here). */}
              <Box sx={{ py: "14px" }}>
                <NavDrawerContext.Provider value={navDrawerValue}>
                  {renderNavBody()}
                </NavDrawerContext.Provider>
              </Box>

              {/* Mobile has no pinned footer to close the list off, so it keeps
                  the hairline here instead. */}
              {isMobile && <Divider sx={{ borderColor: DIVIDER_COLOR }} />}
            </Box>
          </Box>
        </Collapse>

        {/* Bottom breathing room as a real element rather than padding on the
            scroll container, which the browser drops from the scrollable area
            once the body overflows. The negative margin cancels the column gap
            above it so the spacer contributes exactly its own height. Dropped
            once the pinned footer below is in play: that supplies the bottom of
            the dialog, and the menu's own 14px of padding is all the gap the
            last item needs above it. */}
        {(navHidden || isMobile) && (
          <Box
            aria-hidden
            sx={{ flexShrink: 0, height: "36px", mt: "-20px" }}
          />
        )}
      </DialogContent>

      {/* Footer, outside the scroll container so it stays pinned to the bottom
          of the dialog while the body scrolls behind it. Shown with the
          navigation and folded away with it (same 300ms), so a dialog that is
          just the bookmarks grid keeps its plain bottom edge. Touch users have
          no keyboard, so the shortcut hint - and with it the whole footer - is
          desktop only. The lock link is dropped on a screen too narrow to hold
          a page at full width beside the sidebar; the hint stays either way. */}
      {!isMobile && (
        <Collapse
          in={!navHidden}
          timeout={300}
          unmountOnExit
          sx={{ flexShrink: 0 }}
        >
          <Box sx={{ px: "24px", pb: "36px" }}>
            <Divider sx={{ borderColor: DIVIDER_COLOR }} />
            <DashboardShortcutHint />
            {sidebarAvailable && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <DashboardLockLink
                  locked={sidebarLocked}
                  onLock={() => {
                    setSidebarLocked(true);
                    onClose();
                  }}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      )}

      {/* Mobile has no floating X (it would straddle the screen edge), so a
          plain Close button closes the sheet. */}
      {isMobile && (
        <DialogActions sx={{ px: 3, py: 1.5, justifyContent: "center" }}>
          <Button variant="outlined" onClick={onClose} sx={dashboardButtonSx}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
